import React, { Fragment, useCallback, useEffect, useRef } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardText,
  CardTitle,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap";
import { Link, useRouteMatch } from "react-router-dom";
import { connect } from "react-redux";

import {
  clearCourseCreated,
  clearCourseRemoved,
  clearCourseUpdated,
  getListCourses,
  changeCourseStatusClear,
  clearListCourses,
  setCourseRecommendationsClear,
  getCoursesForRecommendations,
  getCoursesForRecommendationsClear,
} from "../../../../store/dashboard/Courses/actions";

import {
  DEFAULT_LIMIT,
  DEFAULT_OFFSET,
} from "../../../../constants/pagination";
import Placeholder from "../../../../components/Common/Placeholder";
import InfiniteScroll from "../../../../components/Common/InfinitScroll";
import { getCourseCategory } from "../../../../store/dashboard/Training/actions";
import useSnackbarState from "../../../../hooks/useSnackbarState";
import CourseTableRow from "../components/CourseTableRow";
import withUploadingError from "../../../../HOCs/withUploadingError";

import styles from "../../dashboard.module.scss";
import SEO from "../../../../components/Common/SEO";

const Courses = ({
  clearCourseCreated,
  courses,
  training,
  getListCourses,
  getCourseCategory,
  clearCourseUpdated,
  clearCourseRemoved,
  changeCourseStatusClear,
  clearListCourses,
  setCourseRecommendationsClear,
  getCoursesForRecommendations,
  getCoursesForRecommendationsClear,
}) => {
  const { data, created, updated, removed, pagination, loading, changedStatus, settedRecommendations } = courses;
  const { item } = training;
  const scrollRef = useRef(null);
  const {
    params: { categoryId },
  } = useRouteMatch("/training/:categoryId");
  const fetchList = (nextPagination = {}) => {
    const { limit = DEFAULT_LIMIT, offset = DEFAULT_OFFSET } = nextPagination;
    getListCourses({ categoryId, model: { limit, offset } });
  };
  const fetchCategory = () => {
    getCourseCategory(categoryId);
  };
  const fetchRecommendationsList = () => {
    getCoursesForRecommendations({});
  };
  const [isUpdated] = useSnackbarState(updated, clearCourseUpdated);
  const [isCreated] = useSnackbarState(created, clearCourseCreated);

  const loadMore = useCallback((offset) => fetchList({ offset }), []);

  useEffect(() => {
    // perform effect if entity appears
    if (removed) {
      fetchList({ limit: DEFAULT_LIMIT, offset: DEFAULT_OFFSET });
      fetchRecommendationsList();
      clearCourseRemoved();
    }
  }, [removed]);

  useEffect(() => {
    // Fetch data
    fetchCategory();
    fetchList();
    fetchRecommendationsList();

    return () => {
      getCoursesForRecommendationsClear();
      clearCourseRemoved();
      clearListCourses();
    };
  }, []);

  useEffect(() => {
    if (!loading && !!changedStatus) {
      changeCourseStatusClear();
    }
  }, [changedStatus, loading]);

  useEffect(() => {
    if (!loading && !!settedRecommendations) {
      setCourseRecommendationsClear();
    }
  }, [settedRecommendations, loading]);

  return (
    <>
      <SEO title="Courses" />
      <Row className="page-content h-100">
        <Container fluid className="h-100">
          <Row>
            <Col xs="12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <h4 className="mb-0 font-size-18">COURSES</h4>
              </div>
            </Col>
          </Row>
          {isCreated && (
            <Row>
              <Col xs="12">
                <Alert color="success">
                  New course has been successfully created
                </Alert>
              </Col>
            </Row>
          )}
          {isUpdated && (
            <Row>
              <Col xs="12">
                <Alert color="success">
                  The course has been successfully updated
                </Alert>
              </Col>
            </Row>
          )}
          <Card className={styles.cardFullsize}>
            <CardBody>
              <CardTitle className="d-flex flex-row justify-content-between pb-3">
                <div className={styles.header}>
                  Category: {item && item.name}
                </div>
                <Link to={`/training/${categoryId}/create`}>
                  <Button color="primary">Create course</Button>
                </Link>
              </CardTitle>
              <CardText tag={"div"}>
                <Row className="h-100 scrollbar-wrapper">
                  {!loading && !data.length && (
                    <Placeholder text="no course created" />
                  )}
                  <InfiniteScroll
                    className="w-100 h-100"
                    ref={scrollRef}
                    disabled={data?.length === 0 || loading}
                    offset={pagination.nextOffset}
                    nextOffset={pagination.nextOffset}
                    totalCount={pagination.totalCount}
                    loadMore={loadMore}
                  >
                    {!!data && !!data.length > 0 && (
                      <div className="table-responsive col-12">
                        <Table className="table mt-0 mb-0 border-bottom">
                          <thead className="thead-light">
                            <tr>
                              <th>Course title</th>
                              <th className="w-220px text-center text-nowrap">
                                Change recommend courses
                              </th>
                              <th className="w-220px text-right pr-5 text-nowrap">
                                Number of courses
                              </th>
                              <th className="w-220px text-right pr-5">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.map((course, index) => (
                              <Fragment key={"table-item-" + index}>
                                <CourseTableRow course={course} />
                              </Fragment>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                    {loading &&
                      !!data.length &&
                      data.length < pagination.totalCount && (
                        <Col xs={12}>
                          <div className="text-center my-3">
                            <div className="text-success">
                              <i className="bx bx-loader bx-spin font-size-18 align-middle mr-2" />{" "}
                              Load more
                            </div>
                          </div>
                        </Col>
                      )}
                  </InfiniteScroll>
                </Row>
              </CardText>
            </CardBody>
          </Card>
        </Container>
      </Row>
    </>
  );
};

const mapStateToProps = (state) => {
  const { Courses, Training } = state;

  return { courses: Courses, training: Training };
};

export default withUploadingError(
  connect(mapStateToProps, {
    clearCourseCreated,
    clearCourseUpdated,
    getListCourses,
    getCourseCategory,
    getCoursesForRecommendations,
    clearCourseRemoved,
    changeCourseStatusClear,
    clearListCourses,
    setCourseRecommendationsClear,
    getCoursesForRecommendationsClear,
  })(Courses)
);
